import i18n from 'i18next';
import { uiLocale } from '../i18n/language';
import { DEFAULT_LANGUAGE } from './languages';

const CSV_FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

// Neutralize spreadsheet-formula injection: a cell starting with =/+/-/@ (or a
// leading tab/CR reaching into one, CWE-1236) can execute as a formula when the
// CSV is opened in Excel/Sheets. Same guard as csv_safe() in
// backend/app/shared/csv_export.py.
export function csvEscape(value: string): string {
  const safe = CSV_FORMULA_PREFIXES.some(prefix => value.startsWith(prefix)) ? `'${value}` : value;
  if (/["',\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

// CSV exports follow the UI language: headers and fixed labels come from the
// csvExport section of the locale files, and backend-built files get the same
// language through ?lang=. What users typed (names, org names) is never translated.

/**
 * A UTF-8 byte-order mark for translated files, so Excel shows accents ("Última")
 * instead of garbling them. English files stay byte-for-byte what they were.
 */
export function csvBom(): string {
  return uiLocale() === DEFAULT_LANGUAGE ? '' : '\uFEFF';
}

/** A download filename in the UI language, from csvExport.fileNames.<key>. */
export function csvFileName(key: string, fallback: string): string {
  return `${i18n.t(`csvExport.fileNames.${key}`, { defaultValue: fallback })}.csv`;
}

/** Tells a backend export which language to write its headers and labels in. */
export function appendCsvLanguage(params: URLSearchParams): URLSearchParams {
  params.set('lang', uiLocale());
  return params;
}

/** A date for a CSV cell, formatted for the UI language. */
export function csvDate(value: string | null | undefined, empty = ''): string {
  return value ? new Date(value).toLocaleDateString(uiLocale()) : empty;
}

export function downloadCsv(header: string[], rows: string[][], filename: string): void {
  const csvContent = csvBom() + [header, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
